import { Component, ElementRef, Input, OnDestroy, OnInit, Pipe, PipeTransform, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { Tools } from "@comunicare/comunicare-helper";
import {
  ALERT_STATUS,
  DataType,
  ICareplan,
  IFilter,
  IObservationDefinition,
  IPatientPageParameter,
  IPatientUser,
  IRuleAlert,
  IRuleDefinition,
  ITranslation,
  IWidgetPatientParameter,
  PatientWidgetName,
  PreferenceContext,
  RESULT_TYPE,
  RULE_TYPE_TARGET,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { HelpData } from "src/app/helpers/helpData";
import { RuleAlert } from "src/app/models/rule-alert.model";
import { TranslateAlertNamePipe } from "src/app/pipes/sharedAlerts.pipe";
import { ExportService } from "src/app/providers/export.service";
import { KnowledgeService } from "src/app/providers/knowledge.service";
import { ObservationsService } from "src/app/providers/observations.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { RuleAlertService } from "src/app/providers/rule-alert.service";
import { SessionService } from "src/app/providers/session.service";
import { TileManager } from "src/app/providers/tile-manager.service";
import { UserService } from "src/app/providers/user.service";
import { WidgetBaseComponent } from "../base/widget-base/widget-base.component";
import { KnowledgeMediaListModalComponent } from "../knowledge-media-list-modal/knowledge-media-list-modal.component";
import { RulesListModalComponent } from "../rules-list-modal/rules-list-modal.component";
import { WidgetActionConfig } from "../widget-actions/widget-actions.component";
import { AlertDetailsComponent } from "./alert-details/alert-details.component";
import { AlertPreferenceComponent, IShowAlertLevel } from "./alert-preference/alert-preference.component";
import { AlertDataSource } from "./alerts-list-datasource";

@Pipe({ name: "getResultsPractitionner" })
export class GetResultsPractitionnerPipe implements PipeTransform {
  transform(ruleDef: IRuleDefinition): ITranslation {
    const result = ruleDef.resultsPractitionner?.filter((r) => r.resultType != RESULT_TYPE.MEDIA)?.[0]?.value;
    return Tools.isDefined(result) ? result : null;
  }
}

@Pipe({ name: "hasResultsPractitionnerMedia" })
export class HasResultsPractitionnerMediaPipe implements PipeTransform {
  transform(ruleDef: IRuleDefinition): boolean {
    const result = ruleDef.resultsPractitionner?.filter((r) => r.resultType == RESULT_TYPE.MEDIA)?.[0]?.value;
    return Tools.isDefined(result);
  }
}
@Component({
  selector: "app-patient-alerts",
  templateUrl: "./patient-alerts.component.html",
  styleUrls: ["./patient-alerts.component.scss", "../base/widget-base/widget-base.component.scss"],
})
export class RuleAlertComponent extends WidgetBaseComponent implements OnInit, OnDestroy {
  private hasAlert$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private isBig$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public actions: WidgetActionConfig[] = [
    {
      type: "button",
      icon: "list_alt",
      label: "rulesList.title",
      ariaLabel: "icon button with a list icon",
      condition$: combineLatest([this.userService.isAuthorized("dashboard/rulesDescription", "GET")]).pipe(
        map(([isAuthorized]) => isAuthorized)
      ),
      isLoading$: this.loading$,
      action: (): unknown => this.onShowRules(),
    },
    {
      type: "button",
      icon: "download",
      label: "observation.export",
      ariaLabel: "icon button with a download icon",
      condition$: combineLatest([
        this.hasAlert$,
        this.userService.isAuthorized("exportAlertsForPatient", "GET"), // Vérifie l'autorisation
      ]).pipe(
        map(([hasAlerts, isAuthorized]) => hasAlerts && isAuthorized) // Combine les deux conditions
      ),
      isLoading$: this.loading$,
      action: (): unknown => this.onExport(),
    },
    {
      type: "button",
      icon: "settings",
      label: "btn.settings",
      ariaLabel: "icon button with a settings icon",
      action: (): unknown => this.onSettings(),
    },
    {
      type: "button",
      icon: "open_in_full",
      label: "btn.details",
      ariaLabel: "icon button with a expand icon",
      condition$: combineLatest([this.hasAlert$, this.responsiveService.isHandset$, this.isBig$]).pipe(
        map(([hasAlerts, isHandset, isBig]) => hasAlerts && !isHandset && !isBig) // Combine les deux conditions
      ),
      action: (): unknown => this.onPlus(),
    },
    {
      type: "button",
      icon: "close_fullscreen",
      label: "btn.reduce",
      ariaLabel: "icon button with a unexpand icon",
      condition$: this.isBig$,
      action: (): unknown => this.onPlus(),
    },
  ];
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<RuleAlert>;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("focus") target: ElementRef;
  public scrollAfterDataInit = false;

  @Input() set patientUser(pu: IPatientUser) {
    if (pu?.user?.caremateIdentifier) {
      this.patientId = pu.user.caremateIdentifier;
      this.patientServiceId = pu.patient?.healthcareservice?.[0]?.reference;
      if (!this.dataSource) {
        this.dataSource = new AlertDataSource(this.translatorService, this.translateNamePipe, this.allDefinitions);
      }
      this.initDataAndAutoRefresh(this.patientId, this.preferenceService.getAutoRefreshWidget());
    }
  }

  @Input() careplans: ICareplan[];
  public patientId: string;
  public alerts: RuleAlert[] = [];
  public allDefinitions: IObservationDefinition[] = [];
  public isBig = false;
  public displayedColumns: string[] = ["date", "status", "level", "origin", "resultsPractitionner", "name", "action"];
  public dataSource: AlertDataSource;
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public lastFilter: IFilter;
  public patientServiceId: string;
  /**
   * IFilter
   */
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;
  public RULE_TYPE_TARGET = RULE_TYPE_TARGET;
  public loading = true;
  private refreshInterval;
  /** Subject that emits when the component has been destroyed. */
  private alertsList$: Subscription;
  private definitions$: Subscription;
  private preferencesList$: Subscription;
  private preferencesUpdate$: Subscription;
  private preferencesUpdate2$: Subscription;
  private showAlerts: IShowAlertLevel = {
    levelOne: false,
    levelTwo: true,
    levelThree: true,
  };

  constructor(
    private alertService: RuleAlertService,
    private preferenceService: PreferenceService,
    private responsiveDialog: ResponsiveDialogService,
    public helpData: HelpData,
    public sessionService: SessionService,
    private tileManager: TileManager,
    private translatorService: TranslateService,
    private observationsService: ObservationsService,
    private translateNamePipe: TranslateAlertNamePipe,
    private exportService: ExportService,
    protected responsiveService: ResponsiveService,
    private dialog: MatDialog,
    private userService: UserService,
    private knowledgeService: KnowledgeService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {
    super(responsiveService);
    this.isBig = TileManager.isBig(PatientWidgetName.ALERTS);
    this.isBig$.next(this.isBig);
  }

  ngOnInit(): void {
    if (!this.dataSource) {
      this.dataSource = new AlertDataSource(this.translatorService, this.translateNamePipe, this.allDefinitions);
    }
    this.sessionService.refreshServerTraductions.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      // the idea is to make the pipes realize there's something that changed without
      // restarting everything. For that we just need to 'change' one of the
      // pipe variables.
      if (this.allDefinitions) {
        // Cloning will make it appear as if this is a new object
        this.allDefinitions = Tools.deepCopy(this.allDefinitions);
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.clearAutoRefresh();
  }

  private initDataAndAutoRefresh(userId: string, _autoRefreshIntervalMs = 60000): void {
    if (this.refreshInterval) {
      this.clearAutoRefresh();
    }
    // this.refreshInterval = setInterval(() => { this.loadData(userId); }, autoRefreshIntervalMs);
    this.loadAllData(userId);
  }

  public clearAutoRefresh(): void {
    clearInterval(this.refreshInterval);
  }

  private loadAllData(id: string): void {
    if (this.preferencesList$) {
      this.preferencesList$.unsubscribe();
    }
    this.preferencesList$ = this.preferenceService
      .list(PreferenceContext.PATIENT_ALERTS_LIST)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((parameters: IWidgetPatientParameter) => {
        if (parameters) {
          this.isBig = this.sessionService.globalPref?.keepLayoutFromPatientPage
            ? parameters.isBig
            : TileManager.isBig(PatientWidgetName.ALERTS);
          this.isBig$.next(this.isBig);
          this.tileManager.updateList(PatientWidgetName.ALERTS, this.isBig);
          if (parameters.showAlertsLevel) {
            this.showAlerts = parameters.showAlertsLevel;
          }
        }
        this.computeColumn();
        this.loadAlerts(id);
      });
  }

  private loadAlerts(id: string): void {
    if (this.alertsList$) {
      this.alertsList$.unsubscribe();
    }
    const alertsLevelsFilter = [];
    if (this.showAlerts.levelOne) {
      alertsLevelsFilter.push("1");
    }
    if (this.showAlerts.levelTwo) {
      alertsLevelsFilter.push("2");
    }
    if (this.showAlerts.levelThree) {
      alertsLevelsFilter.push("3");
    }
    this.alertsList$ = this.alertService
      .listForPatient(id, alertsLevelsFilter.length ? alertsLevelsFilter : null)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((ra) => {
        const filteredAlerts = ra.filter((a) => a.rule.idRule !== null);
        this.alerts = filteredAlerts;
        this.hasAlert$.next(this.alerts?.length ? true : false);
        this.definitions$?.unsubscribe();
        this.definitions$ = this.observationsService
          .listDef(this.patientId)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((defs) => {
            this.allDefinitions = this.keepOnlyUsefulDefinitions(defs, this.alerts);
            this.dataSource.loadData(filteredAlerts);
            this.initPaginatorAndSort();
            if (this.scrollAfterDataInit && this.isBig && this.sessionService.globalPref?.keepLayoutFromPatientPage) {
              this.scroll();
            }
            this.loading = false;
          });
      });
  }

  private keepOnlyUsefulDefinitions(obsDef: IObservationDefinition[], alerts: RuleAlert[]): IObservationDefinition[] {
    const allAlertsLoincs: string[] = [];
    alerts.forEach((alert) => {
      alert.valueComponents.forEach((vc) => {
        allAlertsLoincs.push(vc.code.coding[0].code);
      });
    });
    const definitions: IObservationDefinition[] = obsDef.filter((d) => d.components.find((c) => allAlertsLoincs.includes(c.loinc)));
    return definitions;
  }

  public applyFilter(filter: IFilter): void {
    this.lastFilter = filter;
    this.dataSource.setFilter(filter);
    this.activeFilters = [...(this.dataSource?.getAllFilters() ?? [])];
  }

  public onPlus(): void {
    this.isBig = !this.isBig;
    this.isBig$.next(this.isBig);
    this.tileManager.updateList(PatientWidgetName.ALERTS, this.isBig);
    this.computeColumn();
    this.updatePreference(true);
    this.scroll();
  }

  public updateLastFocus(): void {
    if (this.preferencesUpdate$) {
      this.preferencesUpdate$.unsubscribe();
    }
    this.preferencesUpdate$ = this.preferenceService
      .update({
        context: PreferenceContext.PATIENT_PAGE,
        parameters: {
          lastFocusWidgetName: PatientWidgetName.ALERTS,
        } as IPatientPageParameter,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  public computeColumn(): void {
    if (this.isBig) {
      this.displayedColumns = [
        "date",
        "level",
        "resultsPractitionner",
        "origin",
        "name",
        "status",
        "incharge",
        "lastcomment",
        "patientStatus",
        "patientComment",
        "action",
      ];
    } else {
      this.displayedColumns = ["date", "status", "level", "resultsPractitionner", "origin", "name", "action"];
    }
  }

  public scroll(): void {
    if (this.target) {
      setTimeout(() => {
        this.target.nativeElement.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 100);
    }
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public initPaginatorAndSort(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;
  }

  /**
   * Preferences
   */
  public updatePreference(updateFocus: boolean): void {
    if (this.preferencesUpdate2$) {
      this.preferencesUpdate2$.unsubscribe();
    }
    this.preferencesUpdate2$ = this.preferenceService
      .update({
        context: PreferenceContext.PATIENT_ALERTS_LIST,
        parameters: {
          isBig: this.isBig,
          showAlertsLevel: this.showAlerts,
        } as IWidgetPatientParameter,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (this.isBig && updateFocus) {
          this.updateLastFocus();
        }
      });
  }

  public onDetails(ruleAlert: RuleAlert, createAction: boolean): void {
    this.responsiveDialog
      .open(
        AlertDetailsComponent,
        {
          data: {
            alert: ruleAlert,
            createAction,
            patientServiceId: this.patientServiceId,
            allDefinitions: this.allDefinitions,
          },
        },
        { maxWidth: createAction ? "80vw" : "30vw", maxHeight: "93vh" }
      )
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.applyFilter(this.lastFilter));
  }

  public isAlreadySeen(ruleAlert: RuleAlert): boolean {
    return ruleAlert.alertStatus === ALERT_STATUS.PROCESSED;
  }

  public openAlertHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.patientAlertHelp },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  public onSettings(): void {
    const dialogRef = this.responsiveDialog.open(AlertPreferenceComponent, {
      data: {
        showAlertLevels: this.showAlerts,
      },
      disableClose: true,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: IShowAlertLevel) => {
        if (result) {
          this.showAlerts = result;
          this.updatePreference(false);
          this.loadAlerts(this.patientId);
        }
      });
  }

  public onExport(): void {
    this.loading$.next(true);
    this.exportService
      .exportAlerts(this.patientId, this.sessionService.userLang)
      .then(() => {
        this.loading$.next(false);
      })
      .catch(() => {
        this.snackBar.open(this.translateService.instant("api.errors.download-error"), "ok", { duration: 5000 });
        this.loading$.next(false);
      });
  }

  public showMedia(rule: IRuleAlert): void {
    const mediaId = rule.rule.resultsPractitionner?.filter((r) => r.resultType == RESULT_TYPE.MEDIA)?.[0]?.value[
      this.sessionService.userLang
    ];
    if (mediaId) {
      (rule as any).mediaLoading = true;
      this.knowledgeService
        .getMediaFromIdentifier(mediaId)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((media) => {
          if (media) {
            this.dialog.open(KnowledgeMediaListModalComponent, {
              data: {
                medias: [media],
              },
              minWidth: "50vw",
            });
          }
          (rule as any).mediaLoading = false;
        });
    }
  }

  private onShowRules(): void {
    this.dialog.open(RulesListModalComponent, {
      data: { caremateId: this.patientId, careplans: this.careplans },
      disableClose: false,
    });
  }
}
